import type { TransactionContext } from "../../transaction-context";

export const PROMO_CAMPAIGN_MAX_CODE_COUNT = 10_000;
export const PROMO_CAMPAIGN_MAX_REDEMPTIONS_PER_CODE = 10_000;
export const PROMO_CAMPAIGN_INLINE_MINT_BATCH = 25;
export const PROMO_CAMPAIGN_CRON_MINT_BATCH = 50;
export const PROMO_CAMPAIGN_CODE_LENGTH = 10;
export const PROMO_CAMPAIGN_CODE_ALPHABET = "23456789ABCDEFGHJKLMNPQRSTUVWXYZ";
export const PROMO_CAMPAIGN_CODE_MAX_LENGTH = 64;
export const PROMO_CAMPAIGN_PREFIX_MAX_LENGTH = 24;
export const PROMO_CAMPAIGN_NAME_MAX_LENGTH = 80;
export const PROMO_CAMPAIGN_STRIPE_COUPON_NAME_MAX_LENGTH = 40;
export const PROMO_CAMPAIGN_CODE_INSERT_BATCH = 500;
export const PROMO_CAMPAIGN_MAX_MINT_ATTEMPTS = 8;
export const PROMO_CAMPAIGN_PROCESSING_LEASE_MS = 10 * 60 * 1_000;

export const PROMO_CAMPAIGN_STATUSES = [
  "generating",
  "active",
  "disabling",
  "disabled",
  "failed",
] as const;

export const PROMO_CODE_STATUSES = [
  "pending",
  "minting",
  "active",
  "disable_pending",
  "disabling",
  "disabled",
  "failed",
] as const;

export const PROMO_CAMPAIGN_DISCOUNT_KINDS = ["percent", "amount"] as const;

export type PromoCampaignStatus = (typeof PROMO_CAMPAIGN_STATUSES)[number];
export type PromoCodeStatus = (typeof PROMO_CODE_STATUSES)[number];
export type PromoCampaignDiscountKind =
  (typeof PROMO_CAMPAIGN_DISCOUNT_KINDS)[number];

export type PromoCampaign = {
  id: string;
  name: string;
  codePrefix: string | null;
  exactCode: string | null;
  status: PromoCampaignStatus;
  discountKind: PromoCampaignDiscountKind;
  percentOff: number | null;
  amountOffCents: number | null;
  codeCount: number;
  redemptionsPerCode: number;
  maxRedemptions: number | null;
  expiresAt: Date | null;
  minAmountCents: number | null;
  firstTimeCustomer: boolean;
  stripeCouponId: string | null;
  createdBy: string;
  codesMintedCount: number;
  redemptionsCount: number;
  refundedRedemptionsCount: number;
  lastMintError: string | null;
  createdAt: Date;
  updatedAt: Date;
};

export type PromoCode = {
  id: string;
  campaignId: string;
  code: string;
  status: PromoCodeStatus;
  stripePromotionCodeId: string | null;
  redemptionsCount: number;
  attemptCount: number;
  lastError: string | null;
  nextAttemptAt: Date | null;
  processingStartedAt: Date | null;
  mintedAt: Date | null;
  disabledAt: Date | null;
  createdAt: Date;
  updatedAt: Date;
};

export type PromoRedemption = {
  id: string;
  campaignId: string;
  promoCodeId: string;
  code: string;
  discountCode: string;
  teamId: string;
  teamName: string | null;
  userId: string;
  userEmail: string | null;
  stripeCheckoutSessionId: string;
  stripePromotionCodeId: string;
  subtotalCents: number;
  totalCents: number;
  discountCents: number;
  redeemedAt: Date;
  refundedAt: Date | null;
};

/**
 * The unauthenticated view of a promo code, served by the public /promo page.
 * Deliberately omits campaign ids, mint state, and code inventory: only what a
 * prospective redeemer needs to decide whether to claim the code.
 */
export type PublicPromoCodeDescription = {
  code: string;
  discountKind: PromoCampaignDiscountKind;
  percentOff: number | null;
  amountOffCents: number | null;
  minAmountCents: number | null;
  firstTimeCustomer: boolean;
  expiresAt: Date | null;
};

export type CreatePromoCampaignInput = {
  name: string;
  codePrefix?: string;
  exactCode?: string;
  discountKind: PromoCampaignDiscountKind;
  percentOff?: number;
  amountOffCents?: number;
  codeCount: number;
  redemptionsPerCode: number;
  maxRedemptions?: number;
  expiresAt?: Date;
  minAmountCents?: number;
  firstTimeCustomer?: boolean;
  createdBy: string;
};

export type PromoListCursor = {
  createdAt: Date;
  id: string;
};

export type ListPromoCampaignsInput = {
  limit: number;
  cursor?: string;
};

export type ListPromoCodesInput = {
  campaignId: string;
  limit: number;
  cursor?: string;
};

export type ListPromoRedemptionsInput = {
  campaignId: string;
  limit: number;
  cursor?: string;
};

export type PromoCampaignList = {
  items: PromoCampaign[];
  nextCursor: string | null;
};

export type PromoCodeList = {
  items: PromoCode[];
  nextCursor: string | null;
};

export type PromoRedemptionList = {
  items: PromoRedemption[];
  nextCursor: string | null;
};

export type InsertPromoCampaignInput = PromoCampaign;

export type InsertPromoCodeInput = PromoCode;

export type InsertPromoRedemptionInput = {
  teamId: string;
  userId: string;
  stripeCheckoutSessionId: string;
  stripePromotionCodeId: string;
  discountCode?: string;
  subtotalCents: number;
  totalCents: number;
  discountCents: number;
  redeemedAt: Date;
};

export type MintPromoCodesInput = {
  campaignId?: string;
  limit: number;
};

export type MintPromoCodesResult = {
  attemptedThisRun: number;
  mintedThisRun: number;
  disabledThisRun: number;
  campaignsProcessed: number;
};

export type PromoCampaignWorkSummary = Record<PromoCodeStatus, number>;

/** A joined active promo code plus the campaign that issued it. */
export type ActivePromoCodeWithCampaign = {
  promoCode: PromoCode;
  campaign: PromoCampaign;
};

export type CompletePromoCodeMintResult = {
  completed: boolean;
  needsDisable: boolean;
};

/**
 * The promo service's full public contract: the admin/transport surface plus
 * the redemption recording the Stripe webhook handlers drive inside their
 * transaction context.
 */
export interface PromoCampaignServiceInterface {
  createPromoCampaign(input: {
    input: CreatePromoCampaignInput;
  }): Promise<PromoCampaign>;
  listPromoCampaigns(input: {
    input: ListPromoCampaignsInput;
  }): Promise<PromoCampaignList>;
  getPromoCampaign(input: { campaignId: string }): Promise<PromoCampaign>;
  listPromoCodes(input: { input: ListPromoCodesInput }): Promise<PromoCodeList>;
  listPromoRedemptions(input: {
    input: ListPromoRedemptionsInput;
  }): Promise<PromoRedemptionList>;
  disablePromoCampaign(input: { campaignId: string }): Promise<PromoCampaign>;
  describePromoCode(input: {
    code: string;
  }): Promise<PublicPromoCodeDescription | null>;
  recordPromoRedemption(input: {
    input: InsertPromoRedemptionInput;
    ctx?: TransactionContext;
  }): Promise<boolean>;
  markPromoRedemptionRefunded(input: {
    checkoutSessionId: string;
    refundedAt: Date;
    ctx?: TransactionContext;
  }): Promise<boolean>;
}
